import { createRef } from "react";
import WidgetBase from "../common/widgetBase";
import DataFeed from "./data";
import { Converter } from "../../workspace/converter";

export default class TradingViewNode extends WidgetBase {
  constructor(props) {
    super(props);
    this.containerRef = createRef();
    this.state = {
      tvError: null,
      missingLib: false
    };
    this.tvWidget = null;
    this.mountedMessage = null;
    this.mountedInterval = null;
  }

  componentDidMount() {
    super.componentDidMount();
    this.mountWidget();
  }

  componentWillUnmount() {
    if (this.tvWidget && this.tvWidget.remove) {
      try { this.tvWidget.remove(); } catch (_) {}
      this.tvWidget = null;
    }
    // Without this the widget's store WATCH outlives it: the alias stays watched for the life of the session
    // manager, every `session_object_modified` for it costs a download, and its entry is pinned against
    // eviction. It leaked a listener and a sync hold before the store existed; now it leaks all three.
    super.componentWillUnmount();
  }

  getClassName() {
    return "playground-tradingview-node";
  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    const [ messageValue, warning ] = this.getMessageValue();
    const intervalValue = this.getIntervalValue();
    
    if ( messageValue !== this.mountedMessage || intervalValue !== this.mountedInterval ) {
      if (this.tvWidget && this.tvWidget.remove) {
        try { this.tvWidget.remove(); } catch (_) {}
        this.tvWidget = null;
      }
      this.mountWidget();
    }
  }

  getMessageValue() {
    return this.loadObjectValue(
      this.props.data.component, "message", "message"
    );
  }

  getIntervalValue() {
    return Converter.getComponentPortValueData(this.props.data.component, "interval");
  }

  ensureChartingLibrary(cb) {
    if (window.TradingView && window.TradingView.widget) {
      cb();
      return;
    }

    // Try to load from known local path
    const src = "/libs/charting_library/charting_library.standalone.js";
    const script = document.createElement("script");
    script.src = src;
    script.type = "text/javascript";
    script.onload = () => cb();
    script.onerror = () => {
      this.setState({ missingLib: true });
    };
    document.body.appendChild(script);
  }

  mountWidget() {
    const [ messageValue, warning ] = this.getMessageValue();

    // If we have an error but the new message is valid, clear error to allow render of container
    // and then remount
    if ( this.state.tvError && messageValue && messageValue.message && messageValue.message.metadata ) {
      this.setState({ tvError: null }, () => this.mountWidget());
      return;
    }

    if ( warning || !messageValue ) {
      return;
    }

    if ( !messageValue.message || !messageValue.message.metadata ) {
      this.setState({ tvError: "Invalid message format: missing metadata" });
      return;
    }

    const baseData = messageValue.message.metadata;
    const data = {
      ...baseData,
      widget: { ...(baseData.widget || {}) }
    };

    this.mountedMessage = messageValue;
    const intervalValue = this.getIntervalValue();
    this.mountedInterval = intervalValue;

    if ( intervalValue ) {
        data.widget.interval = intervalValue;
    }

    this.ensureChartingLibrary(() => {
      try {
        const fromTime = Math.floor(data["symbols"][0]["bars"][0]["time"] / 1000);
        const toTime = Math.floor(data["symbols"][0]["bars"].slice(-1)[0]["time"] / 1000);

        const defaultConfig = {
          container: this.getContainerId(),
          locale: "en",
          library_path: "/libs/charting_library/",
          datafeed: new DataFeed(data),
          symbol: data["widget"]["symbol"],
          interval: data["widget"]["interval"],
          fullscreen: false,
          width: "100%",
          height: "100%",
          from: fromTime,
          to: toTime,
          theme: "dark",
          debug: false,
        };

        const config = {...defaultConfig, ...data["widget"]}

        this.tvWidget = new window.TradingView.widget(config);

        // After widget creation, force the time range and configure sessions
        if (this.tvWidget && this.tvWidget.onChartReady) {
          this.tvWidget.onChartReady(() => {
            const chart = this.tvWidget.chart();

            const promises = [];

            // Set time frame to show extended hours
            let premise = chart.setVisibleRange(
              { from: config.from, to: config.to },
              { percentRightMargin: 0 }
            );
            premise.catch(e => console.error(e));
            promises.push(premise);

            // Add studies
            for ( const study of data["studies"] || [] ) {
              let premise = chart.createStudy(
                study.name, 
                false, 
                false, 
                study.inputs, 
                study.overrides || {}, 
                study.options || {}
              );
              premise.catch(e => console.error(e));
              promises.push(premise);
            }

            Promise.all(promises).then(() => {
              // Sometimes there is just not enough wait...
              setTimeout(() => {
                for (const shape of data["shapes"] || []) {
                  chart.createShape(shape["point"], shape["options"]);
                }

                for (const shape of data["multipoint_shapes"] || []) {
                  chart.createMultipointShape(shape["points"], shape["options"]);
                }

                for ( const shape of data["anchored_shapes"] || [] ) {
                  chart.createAnchoredShape(shape["point"], shape["options"]);
                }                
              }, 100);

              // Set price scale mode
              const pane = chart.getPanes()[0];
              const mainScale = pane.getMainSourcePriceScale(); 
              mainScale.setMode(window.TradingView.PriceScaleMode.Normal);
            });
          });
        }
      } catch (e) {
        this.setState({ tvError: "Invalid message format: unexpected parsing error occurred" });
      }
    });
  }

  getContainerId() {
    return `tv_widget_${this.props.data.component.id}`;
  }

  renderBody() {
    // Read the bound object on EVERY path, ahead of every early return. Reading is what holds the watch
    // (session/aliasWatch.js), and a pass that stops reading releases it — so in the two error states below
    // the entry would be dropped by the next `session_object_modified` with nothing left to refetch it, and
    // the self-heal in mountWidget ("we have an error but the new message is valid") would never see a valid
    // message again. The widget sat on "Invalid message format" until the session was re-selected.
    const [ messageValue, notice ] = this.getMessageValue();

    if (this.state.missingLib) {
      return this.renderNotice({ variant: "info", message: "TradingView Charting Library not found." });
    }
    if (this.state.tvError) {
      return this.renderNotice({ variant: "warning", message: this.state.tvError });
    }
    if ( notice ) return this.renderNotice(notice);

    return (
      <div
        id={this.getContainerId()}
        ref={this.containerRef}
        style={{ width: "100%", height: "100%" }}
      />
    );
  }
}
